/* eslint-disable no-console */
import {
  FORMTYPE,
  QuizField,
  QuizItem,
  QuizOption,
} from '@services/common.services'
import _ from 'lodash'

export const shouldShowField = (
  field: QuizField,
  formValues: Record<string, any>
): boolean => {
  if (!field.show_if) return true

  const { field_id, value } = field.show_if
  const currentValue = _.get(formValues, field_id)

  // If currentValue is an array, check if value is an element within it
  if (Array.isArray(currentValue)) {
    // If value is also an array, use _.isEqual for exact match
    if (Array.isArray(value)) {
      return _.isEqual(currentValue, value)
    }
    // If value is a single item, check if it's included in currentValue
    return currentValue.some((item) => _.isEqual(item, value))
  }

  // For non-array currentValue, use _.isEqual for deep comparison
  return _.isEqual(currentValue, value)
}

// utils/helper/quiz.helper.ts
export const transformFormData = (
  formData: Record<string, any>,
  quizItems: QuizItem[]
): Record<string, any> => {
  if (!formData || !quizItems?.length) {
    return { ...formData }
  }

  const result = quizItems.reduce(
    (acc, item) => {
      return (
        item.fields?.reduce((current, field) => {
          if (!field.show_if) return current

          const parentFieldId = field.show_if.field_id
          const childFieldId = field.field_id
          const parentValue = current[parentFieldId]
          const childValue = current[childFieldId]
          const triggerValue = field.show_if.value

          const shouldTransform =
            _.isEqual(parentValue, triggerValue) && childValue !== undefined

          if (shouldTransform) {
            return _.omit({ ...current, [parentFieldId]: childValue }, [
              childFieldId,
            ])
          } else if (childValue !== undefined) {
            return _.omit(current, [childFieldId])
          }
          return current
        }, acc) || acc
      )
    },
    { ...formData }
  )

  const transformValue = _.mapValues(result, (value, key) => {
    // Find the field definition in quizItems to check if it has options
    const field = quizItems
      .flatMap((item) => item.fields || [])
      .find((f) => f.field_id === key)

    if (field?.options && _.isObject(value) && 'value' in value) {
      return value.value // Extract .value if it’s an object with options
    }
    if (
      (field?.form_type === FORMTYPE.CHIP_MULTI_SELECT && _.isArray(value)) ||
      (field?.form_type === FORMTYPE.MULTI_SELECT && _.isArray(value))
    ) {
      return _.map(value, (v: QuizOption) => {
        return _.isObject(v) && 'value' in v ? v?.value : v
      }) // Convert to Date object if it’s a date string
    }

    return value // Leave unchanged otherwise
  })
  return transformValue
}
